import { useCallback, useEffect, useRef } from "react";

const FORM_ID = "EShpWavB4rpn23U8xLYb";
const FORM_ORIGIN = "https://crm.homedesignguild.com";
const FORM_SRC = `${FORM_ORIGIN}/widget/form/${FORM_ID}`;
const FORM_SCRIPT_SRC = `${FORM_ORIGIN}/js/form_embed.js`;
const IFRAME_ID = `inline-${FORM_ID}`;
const LEAD_CAPTURE_STORAGE_KEY = `embedded_iframe_${IFRAME_ID}`;

const hasCompletedLeadCapture = () => {
  if (typeof window === "undefined") return false;

  try {
    const raw = window.localStorage.getItem(LEAD_CAPTURE_STORAGE_KEY);
    if (!raw) return false;

    const parsed = JSON.parse(raw) as { leadCollected?: boolean | string } | null;
    return parsed?.leadCollected === true || parsed?.leadCollected === "true";
  } catch {
    return false;
  }
};

interface PersonalityQuizFormGateModalProps {
  open: boolean;
  onCompleted: () => void;
  resetStoredLeadCaptureOnOpen?: boolean;
}

export const PersonalityQuizFormGateModal = ({
  open,
  onCompleted,
  resetStoredLeadCaptureOnOpen = false,
}: PersonalityQuizFormGateModalProps) => {
  const hasCompletedRef = useRef(false);

  const markCompletedIfSubmitted = useCallback(() => {
    if (hasCompletedRef.current || !hasCompletedLeadCapture()) return;
    hasCompletedRef.current = true;
    onCompleted();
  }, [onCompleted]);

  useEffect(() => {
    if (!open) return;

    hasCompletedRef.current = false;
    if (resetStoredLeadCaptureOnOpen && typeof window !== "undefined") {
      window.localStorage.removeItem(LEAD_CAPTURE_STORAGE_KEY);
    } else {
      markCompletedIfSubmitted();
    }

    const existingScript = document.querySelector<HTMLScriptElement>(
      `script[src="${FORM_SCRIPT_SRC}"]`
    );

    if (!existingScript) {
      const script = document.createElement("script");
      script.src = FORM_SCRIPT_SRC;
      script.async = true;
      document.body.appendChild(script);
    }

    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== FORM_ORIGIN) return;

      const payload = event.data;
      if (!Array.isArray(payload) || payload[0] !== "set-sticky-contacts") return;

      const iframeId = typeof payload[2] === "string" ? payload[2] : "";
      const userDataKey = typeof payload[1] === "string" ? payload[1] : "";
      const isThisForm =
        iframeId === IFRAME_ID || userDataKey === LEAD_CAPTURE_STORAGE_KEY;

      if (!isThisForm) return;

      window.setTimeout(() => {
        markCompletedIfSubmitted();
      }, 80);
    };

    window.addEventListener("message", handleMessage);
    const intervalId = window.setInterval(markCompletedIfSubmitted, 1000);

    return () => {
      window.removeEventListener("message", handleMessage);
      window.clearInterval(intervalId);
    };
  }, [open, markCompletedIfSubmitted, resetStoredLeadCaptureOnOpen]);

  if (!open) return null;

  return (
    <iframe
      src={FORM_SRC}
      style={{ width: "100%", height: "100%", border: "none", borderRadius: "3px" }}
      id={IFRAME_ID}
      data-layout="{'id':'INLINE'}"
      data-trigger-type="alwaysShow"
      data-trigger-value=""
      data-activation-type="alwaysActivated"
      data-activation-value=""
      data-deactivation-type="neverDeactivate"
      data-deactivation-value=""
      data-form-name="Personality Quiz Form"
      data-height="1195"
      data-layout-iframe-id={IFRAME_ID}
      data-form-id={FORM_ID}
      title="Personality Quiz Form"
    />
  );
};
